import { type TFunction } from 'i18next';
import { lowerCase, startCase } from 'lodash';

import { type AtlasGqlUserTemplateOptionFieldsFragment } from 'types/atlas-graphql';

import { type ReportContext, type ReportTemplatesForContext } from '../types';
import { TemplateIdMap } from '../utils';

export const RECENTLY_USED_MENU_KEY = 'recently-used';
export const USER_TEMPLATE_OPTIONS_RETRY_MENU_KEY = 'user-template-options-retry';
export const USER_TEMPLATE_OPTIONS_QUERY_NAME = 'GetUserTemplateOptions';
export const USER_OPTION_MENU_KEY_PREFIX = 'user-option:';
export const USER_TEMPLATE_OPTION_HIGHLIGHTED_CLASS = 'user-template-option-highlighted';
/**
 * How long a row stays highlighted after a pin, unpin or move. The overlay's fade
 * (UserTemplateOptionItem.style.tsx) runs for exactly this long, and the timer in
 * useUserTemplateOptionsMenu drops the highlight when it ends.
 */
export const USER_TEMPLATE_OPTION_HIGHLIGHT_MS = 2000;

export const HERMES_DELIVERY_MODE_NAME = 'Hermes API';
export const SHARE_IN_HORIZON_DELIVERY_MODE_NAME = 'Share in Horizon';

export type UserTemplateOptionFields = AtlasGqlUserTemplateOptionFieldsFragment;

export type ExtraParamFields = {
  hermesId: boolean;
  shareInHorizonOrganization: boolean;
  dateFilter: boolean;
  costRates: boolean;
};

export const getUserOptionMenuKey = (id: string) => `${USER_OPTION_MENU_KEY_PREFIX}${id}`;

export const getUserTemplateOptionItemClassName = ({
  pinned,
  highlighted = false,
}: {
  pinned: boolean;
  highlighted?: boolean;
}) =>
  [
    pinned ? 'user-template-option-pinned' : 'user-template-option-recent',
    highlighted ? USER_TEMPLATE_OPTION_HIGHLIGHTED_CLASS : undefined,
  ]
    .filter(Boolean)
    .join(' ');

export const parseUserOptionMenuKey = (key: string): string | null => {
  if (!key.startsWith(USER_OPTION_MENU_KEY_PREFIX)) {
    return null;
  }
  return key.slice(USER_OPTION_MENU_KEY_PREFIX.length);
};

export const isHermesDeliveryMode = (name?: string | null) => name === HERMES_DELIVERY_MODE_NAME;

export const isShareInHorizonDeliveryMode = (name?: string | null) =>
  name === SHARE_IN_HORIZON_DELIVERY_MODE_NAME;

export const isProjectWorkSummaryTemplate = (templateId: string) =>
  templateId === TemplateIdMap.PROJECT_WORK_SUMMARY_V2;

export const variantIncludesCosts = (variant?: UserTemplateOptionFields['variant']): boolean =>
  !!variant?.configuration?.some(({ field, isIncluded }) => field === 'costs' && isIncluded);

export const getExtraParamFields = (option: UserTemplateOptionFields): ExtraParamFields => {
  const isPws = isProjectWorkSummaryTemplate(option.template.id);
  return {
    hermesId: isHermesDeliveryMode(option.deliveryMode?.name),
    shareInHorizonOrganization: isShareInHorizonDeliveryMode(option.deliveryMode?.name),
    dateFilter: isPws,
    costRates: isPws && variantIncludesCosts(option.variant),
  };
};

export const optionNeedsExtraParams = (option: UserTemplateOptionFields): boolean => {
  const fields = getExtraParamFields(option);
  return (
    fields.hermesId || fields.shareInHorizonOrganization || fields.dateFilter || fields.costRates
  );
};

export const isShareInHorizonOptionDisabled = (
  option: UserTemplateOptionFields,
  shareInHorizonOrganizationCount: number
): boolean =>
  isShareInHorizonDeliveryMode(option.deliveryMode?.name) && shareInHorizonOrganizationCount === 0;

export const formatUserTemplateOptionLabel = (
  t: TFunction,
  option: UserTemplateOptionFields
): string => {
  const context = startCase(lowerCase(option.template.context));
  const templateName = option.template.name;
  const variantName = option.variant?.name;
  // Nullable: a plain template pick is created without one, and that is the
  // commonest recent entry. No segment then, rather than a dangling separator.
  const deliveryMode = option.deliveryMode?.name;

  if (variantName && deliveryMode) {
    return t(
      'reports.userTemplateOptionWithVariant',
      '{{context}}: {{templateName}} ({{variantName}}) - {{deliveryMode}}',
      { context, templateName, variantName, deliveryMode }
    );
  }
  if (variantName) {
    return t(
      'reports.userTemplateOptionWithVariantNoDelivery',
      '{{context}}: {{templateName}} ({{variantName}})',
      { context, templateName, variantName }
    );
  }
  if (deliveryMode) {
    return t('reports.userTemplateOption', '{{context}}: {{templateName}} - {{deliveryMode}}', {
      context,
      templateName,
      deliveryMode,
    });
  }
  return t('reports.userTemplateOptionNoDelivery', '{{context}}: {{templateName}}', {
    context,
    templateName,
  });
};

export const getReportTemplateDisabledTooltip = (
  t: TFunction,
  {
    disabled,
    context,
    templateName,
    itemLimit,
    contextToolTip,
  }: {
    disabled?: boolean;
    context: string;
    templateName: string;
    itemLimit?: number | null;
    contextToolTip?: string;
  }
): string | undefined => {
  if (!disabled) {
    return undefined;
  }
  if (contextToolTip) {
    return contextToolTip;
  }
  return t('reports.reportsLimitedToItems', '{{name}} reports are limited to {{itemLimit}} items', {
    name: `${startCase(lowerCase(context))}:${templateName}`,
    itemLimit,
  });
};

/**
 * Why a pinned/recent option cannot be run right now, or `undefined` if it can. Doubles
 * as the row's tooltip, so the disabled state and the explanation can never disagree.
 */
export const getUserOptionDisabledReason = (
  t: TFunction,
  option: UserTemplateOptionFields,
  {
    augmentedTemplates,
    reportContexts,
  }: {
    augmentedTemplates: ReportTemplatesForContext[];
    reportContexts: ReportContext[];
  }
): string | undefined => {
  const matchingContext = reportContexts.find(
    reportContext => reportContext.context === option.template.context
  );
  const matchingTemplate = augmentedTemplates
    .find(({ context }) => context === option.template.context)
    ?.templates.find(({ template }) => template.id === option.template.id);

  // A stored option outlives its template — retired, made org-scoped, or behind
  // a permission the user has since lost. Without this the row renders enabled
  // and selecting it builds zero report arguments, so nothing is created.
  if (!matchingTemplate) {
    return t('reports.templateNoLongerAvailable', 'This Report template is no longer available');
  }

  return getReportTemplateDisabledTooltip(t, {
    disabled: matchingTemplate.disabled || matchingContext?.optionDisabled,
    context: option.template.context,
    templateName: option.template.name,
    itemLimit: matchingTemplate.template.itemLimit,
    contextToolTip: matchingContext?.toolTip,
  });
};

export const swappedPinOrderIds = (ids: string[], index: number, direction: 'up' | 'down') => {
  const targetIndex = direction === 'up' ? index - 1 : index + 1;
  if (targetIndex < 0 || targetIndex >= ids.length) {
    return ids;
  }
  const next = [...ids];
  [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
  return next;
};

export type UserTemplateOptionKeyboardAction = 'pin' | 'unpin' | 'moveUp' | 'moveDown';

type KeyboardChord = Pick<KeyboardEvent, 'key' | 'altKey' | 'ctrlKey' | 'metaKey'>;

const PIN_TOGGLE_KEY = 'p';
const REORDER_KEYS: Record<string, 'moveUp' | 'moveDown'> = {
  ArrowUp: 'moveUp',
  ArrowDown: 'moveDown',
};

/**
 * The row's own keys: P pins or unpins, Alt+Up / Alt+Down reorder a pinned row. A
 * menuitem's descendants are presentational, so the icon buttons cannot be reached
 * from the keyboard; the shortcuts on the focused row are the accessible path.
 *
 * True for every chord the row owns, including ones that do nothing here (Alt+Arrow
 * on a recent row, Alt+Up on the first pinned row). rc-menu ignores modifiers, so
 * a chord that fell through would double as plain arrow navigation.
 */
export const isUserTemplateOptionShortcut = ({
  key,
  altKey,
  ctrlKey,
  metaKey,
}: KeyboardChord): boolean => {
  if (ctrlKey || metaKey) {
    return false;
  }
  if (altKey) {
    return key in REORDER_KEYS;
  }
  return key.toLowerCase() === PIN_TOGGLE_KEY;
};

export const getUserTemplateOptionKeyboardAction = (
  chord: KeyboardChord,
  {
    pinned,
    isFirst = false,
    isLast = false,
  }: { pinned: boolean; isFirst?: boolean; isLast?: boolean }
): UserTemplateOptionKeyboardAction | null => {
  if (!isUserTemplateOptionShortcut(chord)) {
    return null;
  }
  if (!chord.altKey) {
    return pinned ? 'unpin' : 'pin';
  }
  if (!pinned) {
    return null;
  }
  const action = REORDER_KEYS[chord.key];
  if ((action === 'moveUp' && isFirst) || (action === 'moveDown' && isLast)) {
    return null;
  }
  return action;
};

/** What `aria-keyshortcuts` advertises on a row; must agree with the chords above. */
export const getUserTemplateOptionKeyShortcuts = (pinned: boolean): string =>
  pinned ? 'P Alt+ArrowUp Alt+ArrowDown' : 'P';

/**
 * The row's `aria-description`. A plain attribute rather than `aria-describedby`:
 * antd wraps every menu item in a Tooltip that overwrites `aria-describedby` with
 * the id of a tooltip that is never rendered.
 */
export const getUserTemplateOptionKeyboardHint = (t: TFunction, pinned: boolean): string =>
  pinned
    ? t(
        'reports.pinnedReportKeyboardHint',
        'Press P to unpin this report. Press Alt+Up or Alt+Down to reorder it.'
      )
    : t('reports.recentReportKeyboardHint', 'Press P to pin this report.');

// The reasons double as tooltips, where most of them carry no full stop.
const asSentence = (text: string): string => {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

/**
 * What a screen reader hears with the row. A row that cannot run right now leads
 * with why: on screen the reason is a hover tooltip on the label, which the
 * keyboard never reaches, and the shortcuts still apply to it -- a pinned row that
 * cannot run is exactly the one a user wants to unpin. Whole sentences from two
 * translated strings, not fragments, so the order holds in every locale.
 */
export const getUserTemplateOptionDescription = (
  t: TFunction,
  { pinned, disabledReason }: { pinned: boolean; disabledReason?: string }
): string => {
  const hint = getUserTemplateOptionKeyboardHint(t, pinned);
  return disabledReason ? `${asSentence(disabledReason)} ${hint}` : hint;
};
